import { AuthSessionClosedV1 } from '@omniflow/contracts-core'

const TOKEN_KEY = 'omniflow_token'
/** Локальное время (ms, часы клиента) записи текущего токена — база расписания продления (SESSRENEW O5). */
const RECEIVED_AT_KEY = 'omniflow_token_received_at'
/** Порог продления, присланный сервером вместе с текущим токеном (сек). */
const RENEW_AFTER_KEY = 'omniflow_renew_after_sec'

/**
 * Всё хранение токена — здесь (SESSRENEW R2). `localStorage` в private mode / при
 * запрете хранилища бросает: ошибку пишем в консоль, но не роняем вызывающего —
 * без хранилища вкладка живёт на токене в памяти до перезагрузки.
 */
const storageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch (err) {
    console.error(`auth storage: чтение ${key} не удалось`, err)
    return null
  }
}

const storageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch (err) {
    console.error(`auth storage: запись ${key} не удалась`, err)
  }
}

const storageRemove = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch (err) {
    console.error(`auth storage: удаление ${key} не удалось`, err)
  }
}

const parseStoredNumber = (raw: string | null): number | null => {
  if (raw === null || raw.trim() === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

export interface TokenMeta {
  /** Date.now() в момент записи токена; null — отметки нет (токен записан до SESSRENEW). */
  receivedAt: number | null
  /** Порог продления от сервера; null — сервер не прислал, берётся фолбэк UI. */
  renewAfterSec: number | null
}

export const getToken = (): string | null => storageGet(TOKEN_KEY)

/**
 * Записать токен. Отметка получения ставится всегда; `renewAfterSec` — если
 * передан, иначе прежний порог удаляется (он относился к другому токену).
 */
export const setToken = (token: string, meta?: { renewAfterSec?: number }): void => {
  storageSet(RECEIVED_AT_KEY, String(Date.now()))
  if (meta?.renewAfterSec !== undefined) {
    storageSet(RENEW_AFTER_KEY, String(meta.renewAfterSec))
  } else {
    storageRemove(RENEW_AFTER_KEY)
  }
  // Токен — последним: соседняя вкладка реагирует на событие по ключу токена и должна застать свежую мету (E1).
  storageSet(TOKEN_KEY, token)
}

export const getTokenMeta = (): TokenMeta => ({
  receivedAt: parseStoredNumber(storageGet(RECEIVED_AT_KEY)),
  renewAfterSec: parseStoredNumber(storageGet(RENEW_AFTER_KEY)),
})

export const removeToken = (): void => {
  // Токен — первым: без него соседке нечего продлевать, а мета без токена не читается.
  storageRemove(TOKEN_KEY)
  storageRemove(RECEIVED_AT_KEY)
  storageRemove(RENEW_AFTER_KEY)
}

/**
 * Парсит payload JWT без верификации подписи (браузер).
 *
 * Payload — base64url (`-`/`_`, без `=`), а внутри UTF-8. Голый `atob` на `-`/`_`
 * бросает, и payload молча становился `null`: пользователь с кириллическим именем
 * тенанта входил без ролей (в меню только разделы без гарда). Поэтому — перевод
 * алфавита, паддинг и декодирование байтов как UTF-8.
 */
export const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const segment = token.split('.')[1]
    if (!segment) return null
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const bytes = Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch (err) {
    console.error('parseJwtPayload: payload JWT не разобран', err)
    return null
  }
}

export interface LoginParams {
  username: string
  password: string
  tenantId?: string
}

/**
 * Отказ REST-логина с сохранённым HTTP-статусом: вызывающая сторона (форма
 * входа панели) отличает «неверный пароль» (401) от «сервер недоступен» без
 * парсинга текста сообщения.
 */
export class LoginError extends Error {
  constructor(
    message: string,
    /** HTTP-статус ответа; undefined — до сервера не дошли (сеть/CORS). */
    readonly status?: number,
  ) {
    super(message)
    this.name = 'LoginError'
  }
}

/**
 * REST-логин — единственный HTTP-вызов платформы (дальше всё по NATS WS).
 * `apiBaseUrl` — абсолютный origin (встраиваемая панель живёт на чужом origin);
 * пустая строка = same-origin (apps/ui за nginx/Vite-proxy).
 */
export const login = async (apiBaseUrl: string, params: LoginParams): Promise<{ token: string; renewAfterSec?: number }> => {
  const res = await fetch(`${apiBaseUrl}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  if (!res.ok) {
    throw new LoginError(`login failed: HTTP ${res.status}`, res.status)
  }
  const body = (await res.json()) as { token?: unknown; renewAfterSec?: unknown }
  if (typeof body.token !== 'string') {
    throw new LoginError('login failed: ответ без token', res.status)
  }
  return typeof body.renewAfterSec === 'number'
    ? { token: body.token, renewAfterSec: body.renewAfterSec }
    : { token: body.token }
}

/** Почему сервер закрыл сессию (SESSRENEW O13) — для текста модалки перелогина. */
export type SessionClosedInfo = AuthSessionClosedV1

/**
 * Отказ REST-продления сессии. `status === 401` — терминальный (сессия отозвана/истекла,
 * нужен перелогин); прочий статус или его отсутствие (сеть) — временный, можно повторить.
 * `closed` — объяснение сервера, если сессию закрыл он сам (O13).
 */
export class RenewError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly closed?: SessionClosedInfo,
  ) {
    super(message)
    this.name = 'RenewError'
  }
}

export interface RenewResult {
  token: string
  /** ISO-8601 (UTC) нового срока жизни сессии. */
  expiresAt: string
  renewAfterSec: number
}

/**
 * Продлить логин-сессию: `POST /api/v1/auth/renew` с текущим токеном в Bearer.
 * `signal` — аборт зависшего запроса (дедлайн keeper'а); аборт даёт RenewError без status.
 */
export const renew = async (apiBaseUrl: string, token: string, signal?: AbortSignal): Promise<RenewResult> => {
  let res: Response
  try {
    res = await fetch(`${apiBaseUrl}/api/v1/auth/renew`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      signal: signal ?? null,
    })
  } catch (err) {
    throw new RenewError(`renew failed: ${err instanceof Error ? err.message : String(err)}`)
  }
  if (!res.ok) {
    let code = ''
    let closed: SessionClosedInfo | undefined
    try {
      const errBody = (await res.json()) as { message?: unknown; closed?: unknown }
      if (typeof errBody.message === 'string') code = `: ${errBody.message}`
      if (errBody.closed !== undefined) {
        const parsed = AuthSessionClosedV1.safeParse(errBody.closed)
        // Непонятное объяснение — не повод терять отказ: модалка покажет общий текст.
        if (parsed.success) closed = parsed.data
        else console.warn('renew: поле closed в ответе не распознано', parsed.error.issues)
      }
    } catch {
      // тело ошибки не JSON — статуса достаточно
    }
    throw new RenewError(`renew failed: HTTP ${res.status}${code}`, res.status, closed)
  }
  const body = (await res.json()) as { token?: unknown; expiresAt?: unknown; renewAfterSec?: unknown }
  if (
    typeof body.token !== 'string' ||
    typeof body.expiresAt !== 'string' ||
    typeof body.renewAfterSec !== 'number'
  ) {
    throw new RenewError('renew failed: некорректный ответ', res.status)
  }
  return { token: body.token, expiresAt: body.expiresAt, renewAfterSec: body.renewAfterSec }
}

/**
 * Отозвать сессию на сервере: `POST /api/v1/auth/logout`. Бросает при не-2xx и сетевой ошибке,
 * кроме 401: токен уже истёк или отозван — завершать на сервере нечего, это успех выхода.
 * `keepalive` — запрос переживает закрытие вкладки сразу после клика «Выход».
 * `allDevices` — отозвать все сессии пользователя, а не только эту (O13).
 */
export const logoutRemote = async (
  apiBaseUrl: string,
  token: string,
  opts: { allDevices?: boolean } = {},
): Promise<void> => {
  const res = await fetch(`${apiBaseUrl}/api/v1/auth/logout`, {
    method: 'POST',
    headers: opts.allDevices
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { Authorization: `Bearer ${token}` },
    ...(opts.allDevices ? { body: JSON.stringify({ allDevices: true }) } : {}),
    keepalive: true,
  })
  if (res.status === 401) return
  if (!res.ok) {
    throw new Error(`logout failed: HTTP ${res.status}`)
  }
}

/**
 * Токен ещё живой по claim `exp` (без верификации подписи — это решает сервер).
 * Нужен, чтобы переиспользовать сохранённую сессию, не нарываясь на заведомо
 * протухший JWT: NATS отбил бы его AUTHORIZATION_VIOLATION уже после connect.
 * Токен без `exp` считаем непригодным (лучше форма входа, чем отказ шины).
 */
export const isTokenAlive = (token: string, skewSec = 30): boolean => {
  const claims = parseJwtPayload(token)
  const exp = claims && typeof claims.exp === 'number' ? claims.exp : null
  return exp !== null && exp * 1000 > Date.now() + skewSec * 1000
}
